/**
 * AdminPrivacyPanel — Owner-only utility to audit + scrub email-leak
 * residue in legacy data. Lives at /admin/security/privacy.
 *
 * The panel does TWO things:
 *   1. Audit (read-only):  shows current leak counts on users + comments.
 *   2. Scrub (destructive):
 *        a. DRY-RUN button shows what would change with no writes.
 *        b. APPLY button (with confirm checkbox) actually replaces every
 *           email-shaped `users.name` / `comments.user_name` with a
 *           deterministic OPERATIVE-XXXX placeholder.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Eye, Play, Loader2, CheckCircle2 } from "lucide-react";
import { adminPrivacyAudit, adminPrivacyScrub } from "../../lib/api";

export const AdminPrivacyPanel = ({ token, userToken }) => {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scrubResult, setScrubResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminPrivacyAudit(token, userToken);
      setAudit(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load audit"); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const runScrub = async (dryRun) => {
    setBusy(true);
    try {
      const r = await adminPrivacyScrub(token,
        { confirm: "APPLY", scope: "both", dry_run: !!dryRun }, userToken);
      setScrubResult(r);
      if (dryRun) toast(`Dry-run complete · ${r.users_changed} users · ${r.comments_changed} comments`);
      else toast.success(`Scrub applied · ${r.users_changed} users · ${r.comments_changed} comments`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scrub failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-privacy-panel">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">// PRIVACY MAINTENANCE</div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Email Leak Scrub</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            One-shot Owner utility to sweep legacy data for email-shaped values
            in public-facing fields (users.name · comments.user_name) and replace
            them with deterministic OPERATIVE-XXXX placeholders. New writes are
            already blocked at the API layer; this tool fixes anything stored
            before the policy was in place.
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="privacy-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4" data-testid="privacy-users-card">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">USERS · name field</div>
          <div className="flex items-end gap-3">
            <div className="font-display text-4xl text-[#D92D20]" data-testid="privacy-users-leaks">{audit?.users?.leaks ?? "—"}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">leaks of {audit?.users?.total ?? "—"} total</div>
          </div>
        </div>
        <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4" data-testid="privacy-comments-card">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">COMMENTS · user_name field</div>
          <div className="flex items-end gap-3">
            <div className="font-display text-4xl text-[#D92D20]" data-testid="privacy-comments-leaks">{audit?.comments?.leaks ?? "—"}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">leaks of {audit?.comments?.total ?? "—"} total</div>
          </div>
        </div>
      </div>

      <div className="border border-[#D92D20]/40 bg-[#1a0808] p-5 mb-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="text-[#D92D20] mt-1 shrink-0" size={18} />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">// IRREVERSIBLE MUTATION</div>
            <p className="text-sm text-[#E4DFD3] mb-3">
              The scrub permanently rewrites every email-shaped value found above
              to <span className="font-mono text-[#E0A526]">OPERATIVE-XXXX</span>.
              Run the dry-run first to confirm scope. Once applied, this cannot be
              reversed without restoring from backup.
            </p>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="privacy-confirm-checkbox" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">I understand this is irreversible</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => runScrub(true)} disabled={busy} data-testid="privacy-dryrun-btn"
                className="flex items-center gap-2 border border-[#9FA8D4] text-[#9FA8D4] hover:bg-[#9FA8D4]/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Dry-run
              </button>
              <button onClick={() => runScrub(false)} disabled={busy || !confirmed} data-testid="privacy-apply-btn"
                className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-40">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Apply Scrub
              </button>
            </div>
          </div>
        </div>
      </div>

      {scrubResult && (
        <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4" data-testid="privacy-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={14} className={scrubResult.dry_run ? "text-[#9FA8D4]" : "text-[#5DD4B3]"} />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: scrubResult.dry_run ? "#9FA8D4" : "#5DD4B3" }}>
              {scrubResult.dry_run ? "DRY-RUN" : "APPLIED"} · users={scrubResult.users_changed} · comments={scrubResult.comments_changed}
            </span>
          </div>
          {scrubResult.samples?.length > 0 && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-2">// SAMPLE CHANGES (max 25)</div>
              <div className="font-mono text-xs space-y-1 max-h-64 overflow-y-auto">
                {scrubResult.samples.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 border-b border-[#2D2A26]/40 pb-1">
                    <span className="col-span-2 text-[#757065] uppercase">{s.scope}</span>
                    <span className="col-span-5 text-[#A39E93] truncate">{s.user_id || s.comment_id}</span>
                    <span className="col-span-2 text-[#D92D20]">email</span>
                    <span className="col-span-1 text-[#757065]">→</span>
                    <span className="col-span-2 text-[#E0A526] truncate">{s.after}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPrivacyPanel;
